import ResponsiveRow from './ResponsiveRow';
import { useCallback, useEffect, useRef, useState } from 'react';
import AuthPill from './AuthPill';
import CodeBoxes from './CodeBoxes';
import { INVITE_PASSWORD_NOTICE } from '../../lib/authEmailMarkup';
import { NO_TEXT_CODE } from '../../lib/verificationCodes';

// How long the green "done" pill shows before the screen moves on.
export const AUTH_SUCCESS_PAUSE_MS = 700;
const pause = (ms) => (ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve());
export const SIGN_IN_AFTER_SAVE_ERROR = 'Your password was saved, but we could not sign you in. Please sign in with your new password.';

// codeBoxes: show the code as six large boxes (client app). Display only; the input and its rules are unchanged.
// purpose 'contact_change' (client app, BL-407 round 1b): the code goes to the NEW email or mobile in `change`
// ({ kind: 'email' | 'phone', value }) through the client-contact-change function; a good code finishes the change.
export default function VerificationCodeForm({ client, user, purpose = 'invite', invitationToken = null, change = null, onDone, successPauseMs = AUTH_SUCCESS_PAUSE_MS, codeBoxes = false }) {
    const contactChange = purpose === 'contact_change';
    const portal = purpose === 'portal_login' || contactChange;
    const [delivery, setDelivery] = useState(null);
    const [code, setCode] = useState('');
    const [grant, setGrant] = useState('');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState('');
    const [retryAt, setRetryAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    const mounted = useRef(false);
    const cooldown = Math.max(0, Math.ceil((retryAt - now) / 1000));

    const invoke = useCallback(async (action, values = {}) => {
        const safeError = action === 'verify' ? 'The code is invalid or expired. Resend code and try again.'
            : action === 'complete' ? 'Password was not saved. Resend code to try again.'
            : 'Unable to send your verification code. Please try again.';
        let serverMessage = '';
        try {
            const { data, error: requestError } = await client.functions.invoke(contactChange ? 'client-contact-change' : 'auth-verification', {
                body: contactChange
                    ? { action, kind: change?.kind, value: change?.value, ...values }
                    : { action, purpose, ...(portal ? { invitationToken } : {}), ...values },
            });
            let result = data;
            if (requestError?.context && typeof requestError.context.json === 'function') {
                result = await requestError.context.json();
            }
            if (mounted.current && result?.retryAt) setRetryAt(new Date(result.retryAt).getTime());
            // Only this one server message is shown as-is; everything else stays generic.
            if (result?.error === NO_TEXT_CODE) serverMessage = NO_TEXT_CODE;
            // The contact-change function words its own reasons for the client (cool-down, wrong code, delivery failure).
            if (contactChange && typeof result?.error === 'string' && result.error) serverMessage = result.error;
            if (requestError || !result?.ok) throw new Error();
            if (action === 'verify' && !portal && (typeof result.grant !== 'string' || !result.grant)) throw new Error();
            if (['send', 'resend'].includes(action)
                && (typeof result.id !== 'string' || !result.id || !Array.isArray(result.channels) || !result.channels.length)) throw new Error();
            return result;
        } catch (_) {
            throw new Error(serverMessage || safeError);
        }
    }, [purpose, client, portal, invitationToken, contactChange, change?.kind, change?.value]);

    const send = useCallback(async (action = 'send') => {
        setBusy(true); setError('');
        try {
            const result = await invoke(action);
            if (!mounted.current) return;
            setDelivery(result); setCode(''); setGrant(''); setPassword(''); setConfirmation(''); setSuccess('');
        } catch (e) {
            if (mounted.current) setError(e.message);
        } finally { if (mounted.current) setBusy(false); }
    }, [invoke]);

    useEffect(() => {
        mounted.current = true;
        send();
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => { mounted.current = false; clearInterval(timer); };
    }, [send, user.id]);

    async function submit(event) {
        event.preventDefault();
        if (grant && password !== confirmation) { setError('The passwords do not match.'); return; }
        setBusy(true); setError('');
        try {
            if (!grant) {
                const result = await invoke('verify', { id: delivery.id, code });
                setSuccess('verified');
                await pause(successPauseMs);
                if (!mounted.current) return;
                if (portal) {
                    await onDone?.();
                    return;
                }
                setSuccess(''); setGrant(result.grant); setCode('');
            } else {
                const result = await invoke('complete', { id: delivery.id, grant, password });
                // The server's admin password update signs the user out everywhere (refresh tokens
                // revoked), so the link session can't be refreshed. Sign in fresh with the new
                // password, then finish joining before handing back to app routing.
                const email = result.email || user.email;
                try {
                    if (!email) throw new Error();
                    const { error: signInError } = await client.auth.signInWithPassword({ email, password });
                    if (signInError) throw signInError;
                } catch (_) {
                    throw new Error(SIGN_IN_AFTER_SAVE_ERROR);
                }
                // Already accepted server-side during "complete"; this repeat is idempotent and
                // any real failure is surfaced by sign-in routing in onDone.
                if (purpose === 'invite') { try { await client.rpc('accept_org_invite'); } catch (_) { /* routed by onDone */ } }
                if (!mounted.current) return;
                setSuccess('saved');
                await pause(successPauseMs);
                await onDone?.();
            }
        } catch (e) { if (mounted.current) { setSuccess(''); setError(e.message); } }
        finally { if (mounted.current) setBusy(false); }
    }

    return <form onSubmit={submit} className="auth-verification space-y-4 rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-6 text-[var(--ink)]">
        <h2 className="text-xl font-semibold">{grant ? 'Create your password' : 'Verify your code'}</h2>
        {purpose === 'invite' && <p>{INVITE_PASSWORD_NOTICE}</p>}
        {delivery && !grant && <p role="status">{delivery.channels.includes('email')
            ? `We sent a 6-digit code to ${delivery.maskedEmail}.`
            : 'We sent a 6-digit code to your mobile.'}{delivery.channels.length === 2 ? ' A copy was also sent to your mobile.' : ''}
            {' '}It expires in 10 minutes and can only be used once.</p>}
        {!delivery && !error && <p role="status">Sending your verification code...</p>}
        {grant ? <>
            <label className="block">New password<input required minLength={8} type="password" autoComplete="new-password"
                className="mt-1 block w-full rounded border p-2 bg-[var(--surface-sunken)]"
                value={password} onChange={(event) => setPassword(event.target.value)} /></label>
            <label className="block">Confirm password<input required minLength={8} type="password" autoComplete="new-password"
                className="mt-1 block w-full rounded border p-2 bg-[var(--surface-sunken)]"
                value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        </> : delivery && <label className="block">6-digit code{codeBoxes
            ? <CodeBoxes value={code} onChange={(event) => setCode(event.target.value)} />
            : <input required type="text" pattern="[0-9]{6}" maxLength={6}
                inputMode="numeric" autoComplete="one-time-code" className="mt-1 block w-full rounded border p-2 bg-[var(--surface-sunken)]"
                value={code} onChange={(event) => setCode(event.target.value)} />}</label>}
        {error && <p role="alert">{error}</p>}
        <ResponsiveRow className="flex flex-wrap gap-3">
            {delivery && <AuthPill disabled={busy} type="submit" success={Boolean(success)}
                successLabel={success === 'saved' ? (purpose === 'invite' ? 'Invitation accepted' : 'Password saved') : 'Code verified'}>
                {busy ? 'Working...' : grant ? 'Save password and continue' : 'Verify code'}</AuthPill>}
            {!success && <AuthPill disabled={busy || cooldown > 0} onClick={() => send('resend')}>
                {cooldown ? `Resend code (${cooldown}s)` : 'Resend code'}</AuthPill>}
        </ResponsiveRow>
    </form>;
}
